/**
 * Deterministic pixel-art identicon generator.
 *
 * Given a stable seed (a character id), produces the same avatar every
 * time — a 5×5 vertically-mirrored grid of "pixels" rendered to an
 * inline SVG data URI. This gives every character a distinct, recognisable
 * portrait without any image generation, and the result is identical
 * across panels (casting grid, reference sheet, script-editor circles).
 *
 * Why SVG-as-data-URI rather than a <canvas> or a live DOM grid:
 *   - It's a plain string, so it round-trips through `<img src>`,
 *     CSS `background-image`, and `mask-image` interchangeably.
 *   - It's deterministic and synchronous (no canvas readback), so tests
 *     can assert on it.
 *   - It scales crisply to any avatar size via `shape-rendering`.
 */

/**
 * FNV-1a hash → a stream of pseudo-random bytes derived from the seed.
 * Small, dependency-free, and stable across runtimes (no Math.random).
 */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    // 32-bit FNV prime multiply, kept in unsigned range.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/** A tiny seeded PRNG (mulberry32) so we can draw a deterministic stream. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The identicon palette is intentionally warm and muted to sit inside
 * the studio's editorial cream/ink theme rather than fight it. Each seed
 * picks one foreground hue; the background stays a soft sunk tone.
 */
const FOREGROUND_HUES = [18, 28, 40, 145, 168, 200, 220, 268, 320, 348];

export interface PixelIdenticonOptions {
  /** Grid resolution per axis (mirrored, so effectively ceil(size/2) cols). */
  gridSize?: number;
  /** Logical pixel size of the rendered SVG (square). Default 120. */
  pixelSize?: number;
  /** Background fill. Defaults to a transparent cell so the host shows through. */
  background?: string;
}

/**
 * The raw boolean grid + chosen color, exposed for callers that want to
 * render the identicon themselves (e.g. a live DOM grid) rather than as
 * an <img>. Most callers want {@link pixelIdenticonDataUri} instead.
 */
export interface PixelIdenticonModel {
  grid: boolean[][];
  gridSize: number;
  color: string;
}

export function buildPixelIdenticonModel(
  seed: string,
  gridSize = 5,
): PixelIdenticonModel {
  const baseHash = hashSeed(seed || "anon");
  const random = mulberry32(baseHash);
  const hue = FOREGROUND_HUES[baseHash % FOREGROUND_HUES.length];
  // Slight per-seed saturation/lightness jitter keeps neighbours distinct.
  const saturation = 48 + Math.floor(random() * 16);
  const lightness = 42 + Math.floor(random() * 12);
  const color = `hsl(${hue} ${saturation}% ${lightness}%)`;

  const halfCols = Math.ceil(gridSize / 2);
  const grid: boolean[][] = [];
  for (let row = 0; row < gridSize; row += 1) {
    const cells: boolean[] = new Array(gridSize).fill(false);
    for (let col = 0; col < halfCols; col += 1) {
      // ~46% fill keeps the silhouette readable without being noisy.
      const on = random() > 0.54;
      cells[col] = on;
      cells[gridSize - 1 - col] = on; // mirror horizontally
    }
    grid.push(cells);
  }
  return { grid, gridSize, color };
}

/**
 * Render an identicon as an inline SVG `data:` URI suitable for an
 * `<img src>` or CSS `background-image`.
 */
export function pixelIdenticonDataUri(
  seed: string,
  options: PixelIdenticonOptions = {},
): string {
  const gridSize = options.gridSize ?? 5;
  const pixelSize = options.pixelSize ?? 120;
  const { grid, color } = buildPixelIdenticonModel(seed, gridSize);
  const cell = pixelSize / gridSize;

  const rects: string[] = [];
  for (let row = 0; row < gridSize; row += 1) {
    for (let col = 0; col < gridSize; col += 1) {
      if (!grid[row][col]) continue;
      const x = col * cell;
      const y = row * cell;
      rects.push(
        `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="${color}"/>`,
      );
    }
  }

  const bg = options.background
    ? `<rect width="${pixelSize}" height="${pixelSize}" fill="${options.background}"/>`
    : "";
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${pixelSize}" height="${pixelSize}" ` +
    `viewBox="0 0 ${pixelSize} ${pixelSize}" shape-rendering="crispEdges">` +
    `${bg}${rects.join("")}</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Build a ready-to-mount `<img>` element for a character's identicon
 * avatar. Centralised so every panel renders avatars identically.
 */
export function buildPixelIdenticonImage(
  seed: string,
  options: PixelIdenticonOptions & { alt?: string } = {},
): HTMLImageElement {
  const img = document.createElement("img");
  img.className = "studio-identicon";
  img.src = pixelIdenticonDataUri(seed, options);
  img.alt = options.alt ?? "";
  img.draggable = false;
  return img;
}
